import React, { useEffect, useState } from "react";
import { getAIDailyReview, getAIWeeklyReview, deleteTrade } from "../services/api";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:5000";

export const reviewCache = {};

function getScreenshotSrc(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
}

function fmt(val) {
  const n = Number(val || 0);
  return `${n >= 0 ? "+" : "-"}$${Math.abs(n).toFixed(2)}`;
}

function DayReviewModal({ selectedDate, tradeData, weeklySummary, onClose, onAddTrade, onTradeDeleted }) {
  const [dailyReview, setDailyReview] = useState(null);
  const [weeklyReview, setWeeklyReview] = useState(null);
  const [deletingId, setDeletingId] = useState("");
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!selectedDate) return;

    async function load() {
      // Use cache if available
      if (reviewCache[selectedDate]) {
        setDailyReview(reviewCache[selectedDate].daily);
        setWeeklyReview(reviewCache[selectedDate].weekly);
        return;
      }

      try {
        const [daily, weekly] = await Promise.all([
          getAIDailyReview(selectedDate),
          getAIWeeklyReview(selectedDate),
        ]);
        // Save to cache
        reviewCache[selectedDate] = { daily, weekly };
        setDailyReview(daily);
        setWeeklyReview(weekly);
      } catch {
        setDailyReview(null);
        setWeeklyReview(null);
      }
    }

    load();
  }, [selectedDate]);

  if (!selectedDate || !tradeData) return null;

  const dailyTotal = Number(tradeData.daily_total || 0);
  const trades = tradeData.trades || [];

  async function handleDelete(id) {
    try {
      setDeletingId(id);
      setDeleteError("");
      await deleteTrade(id);
      await onTradeDeleted(selectedDate);
    } catch (err) {
      setDeleteError(err.message || "Failed to delete trade");
    } finally {
      setDeletingId("");
    }
  }

  function ReviewBox({ title, review }) {
    return (
      <div className="section-box premium-box">
        <h4>{title} {review?.locked ? "(Locked)" : "(Live)"}</h4>
        {!review ? (
          <p>Loading...</p>
        ) : (
          <>
            <p><strong>Score:</strong> {review.score}/100</p>
            <p>{review.summary}</p>
            {!review.locked && (
              <>
                <p><strong>Strength:</strong> {review.strengths}</p>
                <p><strong>Weakness:</strong> {review.weakness}</p>
                <p><strong>Next Step:</strong> {review.next_step}</p>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="day-modal-backdrop" onClick={onClose}>
      <div className="day-modal-card" onClick={e => e.stopPropagation()}>
        <div className="day-modal-header">
          <div>
            <p className="day-modal-label">Day Review</p>
            <h2>{selectedDate}</h2>
            <p className={`day-modal-total ${dailyTotal >= 0 ? "green-text" : "red-text"}`}>
              {fmt(dailyTotal)}
            </p>
            <p className="muted-text">{tradeData.trade_count} trade(s)</p>
          </div>

          <div className="day-modal-actions-top">
            <button className="primary-button" onClick={onAddTrade}>+ Add Trade</button>
            <button className="close-button" onClick={onClose}>Close</button>
          </div>
        </div>

        {deleteError && <div className="error-box">{deleteError}</div>}

        <div className="day-modal-content">
          {/* Left column — AI reviews + week summary */}
          <div className="day-modal-left">
            <ReviewBox title="Daily Review" review={dailyReview} />
            <ReviewBox title="Weekly Review" review={weeklyReview} />

            <div className="section-box">
              <h4>Week Summary</h4>
              {weeklySummary ? (
                <>
                  <p><strong>Period:</strong> {weeklySummary.week_start} → {weeklySummary.week_end}</p>
                  <p><strong>Total Profit:</strong> {fmt(weeklySummary.total_profit)}</p>
                  <p><strong>Win Rate:</strong> {Number(weeklySummary.win_rate || 0).toFixed(1)}%</p>
                  <p><strong>Trades:</strong> {weeklySummary.trade_count}</p>
                </>
              ) : (
                <p>No weekly summary yet.</p>
              )}
            </div>
          </div>

          {/* Right column — trade list */}
          <div className="day-modal-right">
            <div className="trade-list">
              <h4>Trades</h4>

              {trades.length === 0 ? (
                <p className="muted-text">No trades on this day yet.</p>
              ) : (
                trades.map((trade, i) => (
                  <div className="trade-card" key={trade.id || i}>
                    <div className="trade-card-header">
                      <div>
                        <p className="trade-label">Trade {i + 1}</p>
                        <p className={`trade-profit ${Number(trade.profit) >= 0 ? "green-text" : "red-text"}`}>
                          {fmt(trade.profit)}
                        </p>
                      </div>
                      <div className="trade-card-header-right">
                        <div className="trade-badges">
                          {trade.session && <span className="trade-pill">{trade.session}</span>}
                          {trade.setup_type && <span className="trade-pill">{trade.setup_type}</span>}
                          {trade.mood && <span className="trade-pill">{trade.mood}</span>}
                        </div>
                        <button
                          className="delete-trade-button"
                          onClick={() => handleDelete(trade.id)}
                          disabled={deletingId === trade.id}
                        >
                          {deletingId === trade.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>

                    <div className="trade-detail-grid">
                      <div className="section-box trade-inner-box">
                        <h4>Confluences</h4>
                        <p>{trade.confluences || "—"}</p>
                      </div>
                      <div className="section-box trade-inner-box">
                        <h4>Reason</h4>
                        <p>{trade.reason || "—"}</p>
                      </div>
                      <div className="section-box trade-inner-box">
                        <h4>Improvements</h4>
                        <p>{trade.improvements || "—"}</p>
                      </div>
                      {trade.notes && (
                        <div className="section-box trade-inner-box">
                          <h4>Notes</h4>
                          <p>{trade.notes}</p>
                        </div>
                      )}
                    </div>

                    {trade.screenshot_url && (
                      <img
                        className="trade-image"
                        src={getScreenshotSrc(trade.screenshot_url)}
                        alt="Trade screenshot"
                        loading="lazy"
                      />
                    )}

                    {(trade.visual_summary || trade.setup_detected || trade.entry_quality || trade.risk_comment) && (
                      <div className="section-box premium-box" style={{ marginTop: "10px" }}>
                        <h4>Visual AI Analysis</h4>
                        {trade.visual_summary && <p><strong>Summary:</strong> {trade.visual_summary}</p>}
                        {trade.setup_detected && <p><strong>Setup:</strong> {trade.setup_detected}</p>}
                        <p><strong>Trend Visible:</strong> {trade.trend_visible ? "Yes" : "No"}</p>
                        {trade.entry_quality && <p><strong>Entry Quality:</strong> {trade.entry_quality}</p>}
                        {trade.risk_comment && <p><strong>Risk Comment:</strong> {trade.risk_comment}</p>}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DayReviewModal;